import React, { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';

import client from '@atom/graph/client';
import { GET_USERS } from '@atom/graph/user';
import { Autocomplete, Avatar, Chip, Icon, IconButton, List } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  UserDetail,
  UsersConnection,
  UsersConnectionInput,
} from '@atom/types/user';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { getUserFullName } from '@atom/utilities/userUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './ardotCreate.module.css';

const { ListItemText } = List;

interface Props {
  updateValue: (users: UserDetail[]) => void;
  value: string[];
  label: string;
  isEdited?: boolean;
  resetAttribute?: () => void;
  initialValue?: Partial<UserDetail>;
  activeValue?: Partial<UserDetail>;
  searchParams?: Partial<UsersConnectionInput>;
  style?: React.CSSProperties;
}

const DEBOUNCE_TIME = 300;
const MIN_CHAR = 3;
const SEARCH_LIMIT = 250;
const SEARCH_PAGE = 1;

type DebouncedUserSearch = (input: UsersConnectionInput) => void;

const styles = {
  chip: {
    marginTop: '5px',
    marginRight: '5px',
    backgroundColor: colors.neutral.ash,
    borderRadius: '20px',
  },
};

const UserSearch = ({
  initialValue,
  activeValue,
  label,
  value,
  updateValue,
  isEdited = false,
  resetAttribute = () => {},
  searchParams = {},
  style: propStyle,
}: Props) => {
  const [query, setQuery] = useState<string>('');
  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [users, setUsers] = useState<UserDetail[]>([]);
  const [selected, setSelected] = useState<UserDetail[]>(
    // @ts-ignore
    activeValue ? [activeValue] : initialValue ? [initialValue] : [],
  );

  const searchUsers = async (input: UsersConnectionInput) => {
    setLoadingSearch(true);

    const { data } = await client.query<{ users: UsersConnection }>({
      query: GET_USERS,
      variables: {
        input: {
          ...input,
          ...searchParams,
          limit: SEARCH_LIMIT,
          page: SEARCH_PAGE,
          showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
        },
      },
    });

    setUsers(data?.users?.users || []);
    setLoadingSearch(false);
  };

  const searchUsersDebounced = useCallback<DebouncedUserSearch>(
    debounce(searchUsers, DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    if (query.length >= MIN_CHAR) {
      searchUsersDebounced({ name: query });
    } else {
      setUsers([]);
    }
  }, [query]);

  useEffect(() => {
    if (!value?.length && selected?.length) {
      setSelected([]);
    }
  }, [value]);

  const handleChange = (values: UserDetail[]) => {
    if (isNilOrEmpty(selected)) {
      setSelected(values);
      updateValue(values);
    }
  };

  const handleDeselect = () => {
    setSelected([]);
    updateValue([]);
  };

  const resetValue = () => {
    resetAttribute();
    // @ts-ignore
    setSelected(initialValue ? [initialValue] : []);
  };

  return (
    <div style={propStyle} className={classes.searchInput}>
      <Autocomplete
        multiple
        fullWidth
        label={label}
        options={users}
        loading={loadingSearch}
        inputValue={query}
        onInputChange={(event, newValue) => setQuery(newValue || '')}
        getOptionLabel={user => getUserFullName(user)}
        placeholder={!selected.length ? 'Search users' : ''}
        value={selected}
        onChange={(event, values) => {
          handleChange(values);
        }}
        getOptionDisabled={user => selected.some(({ id }) => id === user.id)}
        renderOption={(optionProps, user) => {
          return (
            <li key={user.id} {...optionProps}>
              <Avatar
                src={user.photoUrl}
                alt={user.firstName}
                style={{ marginRight: '0.5rem' }}
              />
              <ListItemText
                primary={getUserFullName(user)}
                secondary={user?.email}
              />
            </li>
          );
        }}
        renderValue={values => {
          return values.map(user => (
            <Chip
              key={user.id}
              style={styles.chip}
              avatar={
                <Avatar
                  src={user.photoUrl}
                  alt={user.firstName}
                  style={{ marginRight: '0.5rem' }}
                />
              }
              label={user?.email}
              onDelete={() => handleDeselect()}
            />
          ));
        }}
      />
      {isEdited && (
        <div className={classes.editedControls}>
          Edited
          <IconButton size="small" onClick={resetValue}>
            <Icon>replay</Icon>
          </IconButton>
        </div>
      )}
    </div>
  );
};

export default UserSearch;
